import type { FilesListItem } from '../types';

import { cn } from 'cn';
import { useEffect, useState } from 'react';
import { Icon } from '~/atoms';

type P = {
  file: FilesListItem;
  size?: 'sm' | 'md' | 'lg';
} & ComponentStyling;

export function Thumbnail({ className, file, size = 'md', style }: P) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  const isImage = Boolean(file.type?.startsWith('image/'));

  useEffect(() => {
    // If we don't need an object URL, clean up any existing one
    if (!isImage || file.preview || !file.data) {
      setObjectUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }

        return null;
      });

      return;
    }

    // Create new object URL
    let url: string | null = null;
    try {
      url = URL.createObjectURL(file.data);
      setObjectUrl((prev) => {
        // Clean up previous URL if it exists and is different
        if (prev && prev !== url) {
          URL.revokeObjectURL(prev);
        }

        return url;
      });
    } catch (error) {
      // If URL creation fails, ensure we clean up any previous URL
      setObjectUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }

        return null;
      });

      return;
    }

    // Cleanup function: revoke URL when effect re-runs or component unmounts
    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [file.id, file.data, file.preview, isImage]);

  const previewSrc = file.preview ?? objectUrl;

  const sizeClasses = {
    sm: 'size-6',
    md: 'size-8',
    lg: 'size-12',
  };

  const sizeClassesFallbackIcon = {
    sm: 'size-4',
    md: 'size-6',
    lg: 'size-10',
  };

  if (previewSrc) {
    return (
      <img
        alt={file.name}
        className={cn(
          sizeClasses[size],
          'shrink-0 rounded-sm object-cover',
          className,
        )}
        src={previewSrc}
        style={style}
      />
    );
  }

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center',
        sizeClasses[size],
        className,
      )}
      style={style}
    >
      <Icon
        metaphor="attachment"
        className={cn(
          'text-tiga-text-lighter',
          sizeClassesFallbackIcon[size],
          className,
        )}
        style={style}
      />
    </div>
  );
}
